<template>
  <div class="smile-start-container">
    <!-- Video Container -->
    <div id="video-container" class="video-container">
      <video ref="videoRef" id="webcam" muted playsinline autoplay></video>
    </div>

    <!-- Canvas Container for Three.js -->
    <div id="canvas-container" ref="canvasRef" class="canvas-container"></div>

    <!-- UI Layer -->
    <div id="ui-layer" class="ui-layer">
      <!-- Top: Smile Energy Indicator -->
      <div class="ui-top">
        <div class="smile-energy-indicator">
          <span class="smile-label">SMILE ENERGY</span>
          <div class="smile-bar-container">
            <div 
              class="smile-bar-fill"
              :class="smileScore > SMILE_THRESHOLD ? 'smile-bar-active' : 'smile-bar-inactive'"
              :style="{ width: Math.min(smileScore * 100, 100) + '%' }"
            ></div>
          </div>
          <span 
            class="smile-percentage"
            :class="smileScore > SMILE_THRESHOLD ? 'smile-active' : 'smile-inactive'"
          >
            {{ Math.round(smileScore * 100) }}%
          </span>
        </div>
      </div>

      <!-- Center: Content -->
      <div class="ui-center">
        <!-- Loading State -->
        <div v-if="loading" class="loading-text">INITIALIZING...</div>

        <!-- Intro State -->
        <div v-if="stage === 'intro' && !loading" class="intro-content fade-in">
          <h1 class="intro-title">AI Interview</h1>
          <p class="intro-subtitle">请微笑以开启面试 / Please Smile</p>
          <button class="manual-start-btn" @click="handleStartInterview">
            开启面试
          </button>
        </div>

        <!-- Menu State (Dimensions) -->
        <div v-if="stage === 'menu'" class="menu-content">
          <div 
            v-for="(dim, idx) in dimensions" 
            :key="idx"
            class="dimension-item fade-in"
            :style="{
              left: (50 + (dim.x * 4.0)) + '%', 
              top: (50 - (dim.y * 7)) + '%',
              animationDelay: (1.5 + idx * 0.15) + 's'
            }"
          >
            <div class="dimension-wrapper">
              <div class="dimension-line"></div>
              <span class="dimension-label">{{ dim.label }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { FilesetResolver, FaceLandmarker } from '@mediapipe/tasks-vision'

// Configuration
const PARTICLE_COUNT = 8000
const SMILE_THRESHOLD = 0.5
const COLOR_HEXAGON = new THREE.Color(0x00ffff) // Cyan
const COLOR_STARS = new THREE.Color(0xffd700)   // Gold

// Evaluation Dimensions (Zigzag Constellation Layout)
const DIMENSIONS = [
  { label: "专业能力", x: -6.5, y: -2.0 },
  { label: "沟通表达", x: -4.0, y: 1.5 },
  { label: "逻辑思维", x: -1.5, y: -1.0 },
  { label: "简历匹配", x: 1.5, y: 2.5 },
  { label: "成长潜力", x: 4.5, y: -0.5 },
  { label: "抗压能力", x: 7.0, y: 2.0 },
]

// Helper Function
function getRandomHexagonPoint(radius) {
  const segment = Math.floor(Math.random() * 6)
  const angleStart = (segment * Math.PI) / 3
  const angleEnd = ((segment + 1) * Math.PI) / 3
  
  let r1 = Math.random()
  let r2 = Math.random()
  if (r1 + r2 > 1) { 
    r1 = 1 - r1
    r2 = 1 - r2
  }

  const spread = 0.85 + Math.random() * 0.15

  const A = { x: radius * Math.cos(angleStart), y: radius * Math.sin(angleStart) }
  const B = { x: radius * Math.cos(angleEnd), y: radius * Math.sin(angleEnd) }

  const x = (A.x * r1 + B.x * r2) * spread
  const y = (A.y * r1 + B.y * r2) * spread
  const z = (Math.random() - 0.5) * 1.5
  
  return { x, y, z }
}

export default {
  name: 'SmileStart',
  props: {
    visible: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      smileScore: 0,
      stage: 'intro', // "intro" | "transition" | "menu"
      loading: true,
      dimensions: DIMENSIONS,
      SMILE_THRESHOLD: SMILE_THRESHOLD,
      // Non-reactive properties
      sceneLogic: {},
      animationId: null,
      detectionAnimationId: null, // 微笑检测循环的 ID
      cleanupThree: null,
      stream: null,
      faceLandmarker: null,
      handleResize: null,
      initialized: false,
      cameraInitialized: false,
      mediaPipePreloaded: false, // MediaPipe 资源是否已预加载
      // 如果 Three.js WebGL 初始化失败，则强制让 MediaPipe 只用 CPU
      forceCpuMediaPipe: false
    }
  },
  watch: {
    smileScore() {
      this.checkTransition()
    },
    stage() {
      this.checkTransition()
    },
    loading() {
      this.checkTransition()
    },
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (!this.cleanupThree) {
            this.cleanupThree = this.initThreeJS()
            this.initialized = true
          }
          
          if (!this.mediaPipePreloaded) {
            this.preloadMediaPipe()
          }
          
          if (!this.cameraInitialized) {
            this.initCamera()
          }
        })
      } else if (!newVal && this.stream) {
        this.stopCamera()
      }
    }
  },
  mounted() {
    if (this.initialized) return
    this.cleanupThree = this.initThreeJS()
    this.initialized = true
    
    this.preloadMediaPipe()
    
    if (this.visible && !this.cameraInitialized) {
      this.$nextTick(() => {
        this.initCamera()
      })
    }
  },
  beforeDestroy() {
    if (this.detectionAnimationId !== null) {
      cancelAnimationFrame(this.detectionAnimationId)
      this.detectionAnimationId = null
    }
    
    if (this.faceLandmarker) {
      try {
        if (typeof this.faceLandmarker.close === 'function') {
          this.faceLandmarker.close()
        }
      } catch (error) {
        console.warn('[SmileStart] 清理 FaceLandmarker 时出错:', error)
      } finally {
        this.faceLandmarker = null
      }
    }
    
    if (this.cleanupThree) {
      this.cleanupThree()
      this.cleanupThree = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach(t => {
        t.stop()
        t = null
      })
      this.stream = null
    }
    if (this.$refs.videoRef) {
      this.$refs.videoRef.srcObject = null
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize)
      this.handleResize = null
    }
    this.cameraInitialized = false
    this.mediaPipePreloaded = false
    this.initialized = false
  },
  methods: {
    checkTransition() {
      if (this.smileScore > this.SMILE_THRESHOLD && this.stage === 'intro' && !this.loading) {
        this.handleTransition()
      }
    },

    handleTransition() {
      this.stage = "transition"
      
      if (this.sceneLogic.explode) {
        this.sceneLogic.explode()
      }

      setTimeout(() => {
        if (this.sceneLogic.morphToStars) {
          this.sceneLogic.morphToStars()
        }
      }, 800)

     this.stage = "menu"

      setTimeout(() => {
        this.handleStartInterview()
      }, 3000) 
    },

    handleBack() {
      this.stage = "intro"
      if (this.sceneLogic.morphToHexagon) {
        this.sceneLogic.morphToHexagon()
      }
    },

    handleStartInterview() {
      // Emit event to parent component
      this.$emit('start-interview')
    },

    async preloadMediaPipe() {
      if (this.mediaPipePreloaded || this.faceLandmarker) return
      
      try {
        const wasmPath = '/mediapipe-wasm'
        const vision = await FilesetResolver.forVisionTasks(wasmPath)
        const modelUrl = '/face_landmarker.task'

        // 如果前面 Three.js WebGL 已经失败（forceCpuMediaPipe = true），
        // 说明当前机器图形能力很差，直接跳过微笑检测，进入下一步，避免再让 MediaPipe 报错卡死。
        if (this.forceCpuMediaPipe) {
          console.warn('[SmileStart] 由于 WebGL 环境受限，跳过微笑检测，直接进入面试下一步')
          this.loading = false
          this.handleStartInterview()
          return
        }

        // 否则：先检测当前环境 WebGL 能力：
        // - 有 WebGL 就优先尝试 GPU
        // - 没有 / 检测失败则直接用 CPU
        let delegate = 'GPU'
        try {
          const testCanvas = document.createElement('canvas')
          const gl =
            testCanvas.getContext('webgl2') ||
            testCanvas.getContext('webgl') ||
            testCanvas.getContext('experimental-webgl')
          if (!gl) {
            delegate = 'CPU'
            console.warn('[SmileStart] WebGL 不可用，FaceLandmarker 降级为 CPU 模式')
          }
        } catch (e) {
          delegate = 'CPU'
          console.warn('[SmileStart] 检测 WebGL 能力失败，FaceLandmarker 直接使用 CPU 模式:', e)
        }

        // 优先按上面判断的 delegate 初始化
        try {
          this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: modelUrl,
              delegate
            },
            outputFaceBlendshapes: true,
            runningMode: 'VIDEO',
            numFaces: 1
          })
        } catch (err) {
          // 如果是 GPU 失败，自动降级 CPU 再试一次
          if (delegate === 'GPU') {
            console.warn('[SmileStart] GPU delegate 初始化失败，尝试降级为 CPU:', err)
            try {
              this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
                baseOptions: {
                  modelAssetPath: modelUrl,
                  delegate: 'CPU'
                },
                outputFaceBlendshapes: true,
                runningMode: 'VIDEO',
                numFaces: 1
              })
            } catch (err2) {
              console.warn('[SmileStart] CPU delegate 初始化也失败，放弃表情检测，直接进入下一步:', err2)
              this.faceLandmarker = null
              this.mediaPipePreloaded = false
              this.loading = false
              // 兜底：直接放行进入面试，避免卡死在微笑启动
              this.handleStartInterview()
              return
            }
          } else {
            console.warn('[SmileStart] MediaPipe 预加载失败，将在需要时重试:', err)
            this.faceLandmarker = null
            this.mediaPipePreloaded = false
            return
          }
        }
        
        this.mediaPipePreloaded = true
        console.log('[SmileStart] MediaPipe 资源预加载完成 (delegate: ' + (this.faceLandmarker ? '已初始化' : '未初始化') + ')')
      } catch (err) {
        console.warn('[SmileStart] MediaPipe 预加载失败，将在需要时重试:', err)
        this.faceLandmarker = null
        this.mediaPipePreloaded = false
      }
    },

    async initCamera() {
      if (this.cameraInitialized) return // 防止重复初始化摄像头
      
      try {
        if (!this.faceLandmarker) {
          await this.preloadMediaPipe()
        }
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: 1280, height: 720, facingMode: "user" } 
        })
        
        if (this.$refs.videoRef) {
          this.$refs.videoRef.srcObject = this.stream
          this.$refs.videoRef.play()
        }

        await this.initFaceLandmarker(this.$refs.videoRef)
        
        this.cameraInitialized = true
        this.loading = false
      } catch (err) {
        console.error("Camera initialization error:", err)
        this.$message.error("请允许摄像头权限以继续 / Please allow camera access")
      }
    },
    
    stopCamera() {
      if (this.detectionAnimationId !== null) {
        cancelAnimationFrame(this.detectionAnimationId)
        this.detectionAnimationId = null
      }
      
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      if (this.$refs.videoRef) {
        this.$refs.videoRef.srcObject = null
      }
      this.cameraInitialized = false
    },

    async initFaceLandmarker(videoEl) {
      // 统一走 preloadMediaPipe 的逻辑（内部已经做了 GPU -> CPU 的降级处理）
      if (!this.faceLandmarker) {
        await this.preloadMediaPipe()
        if (!this.faceLandmarker) {
          console.warn('[SmileStart] FaceLandmarker 未成功初始化，跳过微笑检测循环')
          return
        }
      }

      const detect = () => {
        if (!videoEl || !this.faceLandmarker || this.detectionAnimationId === null) {
          return
        }
        
        if (videoEl.currentTime > 0 && !videoEl.paused && !videoEl.ended) {
          try {
            const results = this.faceLandmarker.detectForVideo(videoEl, performance.now())
            if (results.faceBlendshapes && results.faceBlendshapes.length > 0) {
              const shapes = results.faceBlendshapes[0].categories
              const smileLeft = shapes.find(s => s.categoryName === "mouthSmileLeft")?.score || 0
              const smileRight = shapes.find(s => s.categoryName === "mouthSmileRight")?.score || 0
              const score = (smileLeft + smileRight) / 2
              // Smooth the score
              this.smileScore = this.smileScore * 0.9 + score * 0.1
            }
          } catch (e) {
            // Ignore temporary detection errors
          }
        }
        if (this.detectionAnimationId !== null) {
          this.detectionAnimationId = requestAnimationFrame(detect)
        }
      }
      this.detectionAnimationId = requestAnimationFrame(detect)
    },

    initThreeJS() {
      const container = this.$refs.canvasRef
      if (!container) return null
      
      // 先清理旧的 renderer（如果存在）
      if (this.cleanupThree) {
        this.cleanupThree()
        this.cleanupThree = null
      }
      
      // 确保容器完全清空，移除所有子元素（包括可能存在的 canvas）
      while (container.firstChild) {
        const child = container.firstChild
        // 如果是 canvas，先尝试获取并释放其上下文
        if (child.tagName === 'CANVAS') {
          const gl = child.getContext('webgl') || child.getContext('webgl2') || child.getContext('experimental-webgl')
          if (gl) {
            const loseContext = gl.getExtension('WEBGL_lose_context')
            if (loseContext) {
              loseContext.loseContext()
            }
          }
        }
        container.removeChild(child)
      }

      // 优先使用容器尺寸，如果容器被隐藏则使用窗口尺寸
      let width = container.clientWidth
      let height = container.clientHeight
      
      // 如果容器尺寸为 0（可能被隐藏），使用窗口尺寸
      if (width === 0 || height === 0) {
        width = window.innerWidth
        height = window.innerHeight
      }
      
      // 如果窗口尺寸也为 0，则无法初始化
      if (width === 0 || height === 0) {
        console.warn('[SmileStart] Container and window dimensions are 0, cannot initialize Three.js')
        return null
      }

      // 在创建 Three.js 渲染器之前，先检测当前环境是否支持 WebGL，
      // 避免在老旧浏览器 / 关闭硬件加速时抛出 "webGL context could not be created" 并卡死页面
      const testCanvas = document.createElement('canvas')
      const testGl =
        testCanvas.getContext('webgl') ||
        testCanvas.getContext('experimental-webgl')
      if (!testGl) {
        console.warn('[SmileStart] WebGL not supported, skip particle effect (fallback to simple camera view).')
        // 同时标记：当前环境 WebGL 能力较差，MediaPipe 也强制只用 CPU
        this.forceCpuMediaPipe = true
        return null
      }

      const scene = new THREE.Scene()
      const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)
      camera.position.z = 14

      let renderer
      try {
        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
        renderer.setSize(width, height)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        container.appendChild(renderer.domElement)
      } catch (error) {
        console.warn('[SmileStart] Failed to create WebGLRenderer, disable particle effect:', error)
        // Three.js 的 WebGLRenderer 创建失败，说明当前 WebGL 不稳定，
        // 为了安全起见，同时让 MediaPipe 强制使用 CPU delegate
        this.forceCpuMediaPipe = true
        return null
      }

      // --- Constellation Line ---
      const linePoints = DIMENSIONS.map(d => new THREE.Vector3(d.x, d.y, 0))
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(linePoints)
      const lineMaterial = new THREE.LineBasicMaterial({ 
        color: 0xffffff, 
        transparent: true, 
        opacity: 0.0 
      })
      const constellationLine = new THREE.Line(lineGeometry, lineMaterial)
      scene.add(constellationLine)

      // --- Particles Data ---
      const geometry = new THREE.BufferGeometry()
      const positions = new Float32Array(PARTICLE_COUNT * 3)
      const targetPositions = new Float32Array(PARTICLE_COUNT * 3)
      const colors = new Float32Array(PARTICLE_COUNT * 3)
      const sizes = new Float32Array(PARTICLE_COUNT)
      const randoms = new Float32Array(PARTICLE_COUNT)
      
      const particleMetadata = new Array(PARTICLE_COUNT).fill(0).map(() => ({
        angle: 0,
        radius: 0,
        speed: 0,
        clusterIdx: -1,
        yOffset: 0
      }))

      // Initial State: Random Hexagon
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const { x, y, z } = getRandomHexagonPoint(6)
        positions[i * 3] = x
        positions[i * 3 + 1] = y
        positions[i * 3 + 2] = z
        
        targetPositions[i * 3] = x
        targetPositions[i * 3 + 1] = y
        targetPositions[i * 3 + 2] = z

        colors[i * 3] = COLOR_HEXAGON.r
        colors[i * 3 + 1] = COLOR_HEXAGON.g
        colors[i * 3 + 2] = COLOR_HEXAGON.b

        sizes[i] = Math.random() * 0.5 + 0.2
        randoms[i] = Math.random()
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))
      geometry.setAttribute('aRandom', new THREE.BufferAttribute(randoms, 1))

      const material = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          pixelRatio: { value: renderer.getPixelRatio() },
          uSmile: { value: 0.0 },
        },
        vertexShader: `
          attribute float size;
          attribute vec3 color; 
          attribute float aRandom;
          varying vec3 vColor;
          varying float vAlpha;
          uniform float time;
          uniform float pixelRatio;
          uniform float uSmile;
          
          vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
          vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
          vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
          vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
          float snoise(vec3 v) {
            const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
            const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
            vec3 i  = floor(v + dot(v, C.yyy) );
            vec3 x0 = v - i + dot(i, C.xxx) ;
            vec3 g = step(x0.yzx, x0.xyz);
            vec3 l = 1.0 - g;
            vec3 i1 = min( g.xyz, l.zxy );
            vec3 i2 = max( g.xyz, l.zxy );
            vec3 x1 = x0 - i1 + C.xxx;
            vec3 x2 = x0 - i2 + C.yyy;
            vec3 x3 = x0 - D.yyy;
            i = mod289(i);
            vec4 p = permute( permute( permute(
                                   i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                               + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
                               + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
            float n_ = 0.142857142857;
            vec3  ns = n_ * D.wyz - D.xzx;
            vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
            vec4 x_ = floor(j * ns.z);
            vec4 y_ = floor(j - 7.0 * x_ );
            vec4 x = x_ *ns.x + ns.yyyy;
            vec4 y = y_ *ns.x + ns.yyyy;
            vec4 h = 1.0 - abs(x) - abs(y);
            vec4 b0 = vec4( x.xy, y.xy );
            vec4 b1 = vec4( x.zw, y.zw );
            vec4 s0 = floor(b0)*2.0 + 1.0;
            vec4 s1 = floor(b1)*2.0 + 1.0;
            vec4 sh = -step(h, vec4(0.0));
            vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
            vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
            vec3 p0 = vec3(a0.xy,h.x);
            vec3 p1 = vec3(a0.zw,h.y);
            vec3 p2 = vec3(a1.xy,h.z);
            vec3 p3 = vec3(a1.zw,h.w);
            vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
            p0 *= norm.x;
            p1 *= norm.y;
            p2 *= norm.z;
            p3 *= norm.w;
            vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
            m = m * m;
            return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),
                                                          dot(p2,x2), dot(p3,x3) ) );
          }

          void main() {
            vec3 pos = position;
            
            float breathe = sin(time * 1.5 + pos.y * 0.5) * 0.1;
            float noiseVal = snoise(vec3(pos.x * 0.5, pos.y * 0.5, time * 2.0));
            vec3 jitter = vec3(noiseVal) * uSmile * 0.5;
            pos += jitter;
            pos *= (1.0 + uSmile * 0.05 * sin(time * 10.0));

            float twinkle = sin(time * 3.0 + aRandom * 10.0) * 0.4 + 0.6;
            vColor = mix(color * twinkle, vec3(1.0), uSmile * 0.3);
            
            vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
            gl_PointSize = size * pixelRatio * (80.0 / -mvPosition.z) * (1.0 + uSmile * 0.5);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          void main() {
            vec2 xy = gl_PointCoord.xy - vec2(0.5);
            float r = length(xy);
            if (r > 0.5) discard;
            
            float glow = 1.0 - (r * 2.0);
            glow = pow(glow, 1.5);
            float core = smoothstep(0.1, 0.0, r);
            vec3 finalColor = vColor + vec3(core * 0.6);
            gl_FragColor = vec4(finalColor, glow);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      })

      const particles = new THREE.Points(geometry, material)
      scene.add(particles)

      // --- Transition Logics ---
      let mode = 'HEXAGON'

      // Store methods in the non-reactive sceneLogic object
      this.sceneLogic.explode = () => {
        mode = 'EXPLODE'
        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const dirX = (Math.random() - 0.5) * 2
          const dirY = (Math.random() - 0.5) * 2
          const dirZ = (Math.random() - 0.5) * 2
          const len = Math.sqrt(dirX*dirX + dirY*dirY + dirZ*dirZ)
          const force = 30 + Math.random() * 20
          targetPositions[i*3] = (dirX/len) * force
          targetPositions[i*3+1] = (dirY/len) * force
          targetPositions[i*3+2] = (dirZ/len) * force
        }
      }

      this.sceneLogic.morphToStars = () => {
        mode = 'STARS'
        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const clusterIdx = i % DIMENSIONS.length
          
          const angle = Math.random() * Math.PI * 2
          const radius = Math.random() * 1.5
          const speed = 0.5 + Math.random() * 0.5
          
          particleMetadata[i].angle = angle
          particleMetadata[i].radius = radius
          particleMetadata[i].clusterIdx = clusterIdx
          particleMetadata[i].speed = speed
          particleMetadata[i].yOffset = (Math.random() - 0.5) * 1.0
        }
      }

      this.sceneLogic.morphToHexagon = () => {
        mode = 'HEXAGON'
        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const { x, y, z } = getRandomHexagonPoint(6)
          targetPositions[i * 3] = x
          targetPositions[i * 3 + 1] = y
          targetPositions[i * 3 + 2] = z
        }
      }

      const clock = new THREE.Clock()

      const animate = () => {
        this.animationId = requestAnimationFrame(animate)
        const time = clock.getElapsedTime()
        
        // Access current value directly from Vue data
        const currentSmile = this.smileScore
        
        material.uniforms.time.value = time
        material.uniforms.uSmile.value = currentSmile

        const posAttr = geometry.attributes.position
        const colAttr = geometry.attributes.color

        let moveSpeed = 0.05
        if (mode === 'EXPLODE') moveSpeed = 0.1

        if (mode === 'HEXAGON') {
          const baseRotSpeed = 0.1
          const smileRotSpeed = currentSmile * 2.0
          particles.rotation.z += (baseRotSpeed + smileRotSpeed) * 0.01
          particles.rotation.y = Math.sin(time * 0.2) * 0.1
          constellationLine.material.opacity = THREE.MathUtils.lerp(constellationLine.material.opacity, 0, 0.05)

        } else if (mode === 'EXPLODE') {
          particles.rotation.z *= 0.95
          constellationLine.material.opacity = THREE.MathUtils.lerp(constellationLine.material.opacity, 0, 0.1)

        } else if (mode === 'STARS') {
          particles.rotation.z = THREE.MathUtils.lerp(particles.rotation.z, 0, 0.05)
          particles.rotation.y = 0
          constellationLine.material.opacity = THREE.MathUtils.lerp(constellationLine.material.opacity, 0.25, 0.02)
        }

        if (mode === 'STARS') {
          for (let i = 0; i < PARTICLE_COUNT; i++) {
            const meta = particleMetadata[i]
            const dim = DIMENSIONS[meta.clusterIdx]
            
            meta.angle += meta.speed * 0.01
            
            const tx = dim.x + Math.cos(meta.angle) * meta.radius
            const ty = dim.y + Math.sin(meta.angle) * meta.radius
            const tz = meta.yOffset

            targetPositions[i * 3] = tx
            targetPositions[i * 3 + 1] = ty
            targetPositions[i * 3 + 2] = tz
          }
        }

        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const px = posAttr.array[i * 3]
          const py = posAttr.array[i * 3 + 1]
          const pz = posAttr.array[i * 3 + 2]

          const tx = targetPositions[i * 3]
          const ty = targetPositions[i * 3 + 1]
          const tz = targetPositions[i * 3 + 2]

          posAttr.array[i * 3] += (tx - px) * moveSpeed
          posAttr.array[i * 3 + 1] += (ty - py) * moveSpeed
          posAttr.array[i * 3 + 2] += (tz - pz) * moveSpeed

          let targetColor = COLOR_HEXAGON
          if (mode === 'STARS' || mode === 'EXPLODE') targetColor = COLOR_STARS

          colAttr.array[i * 3] += (targetColor.r - colAttr.array[i * 3]) * moveSpeed
          colAttr.array[i * 3 + 1] += (targetColor.g - colAttr.array[i * 3 + 1]) * moveSpeed
          colAttr.array[i * 3 + 2] += (targetColor.b - colAttr.array[i * 3 + 2]) * moveSpeed
        }

        posAttr.needsUpdate = true
        colAttr.needsUpdate = true

        renderer.render(scene, camera)
      }

      animate()

      this.handleResize = () => {
        const w = container.clientWidth || window.innerWidth
        const h = container.clientHeight || window.innerHeight
        camera.aspect = w / h
        camera.updateProjectionMatrix()
        renderer.setSize(w, h)
      }
      window.addEventListener("resize", this.handleResize)

      return () => {
        if (this.handleResize) {
          window.removeEventListener("resize", this.handleResize)
          this.handleResize = null
        }
        if (this.animationId) {
          cancelAnimationFrame(this.animationId)
          this.animationId = null
        }
        if (renderer) {
          try {
            renderer.dispose()
            const canvas = renderer.domElement
            if (canvas) {
              const gl = canvas.getContext('webgl') || canvas.getContext('webgl2') || canvas.getContext('experimental-webgl')
              if (gl) {
                const loseContext = gl.getExtension('WEBGL_lose_context')
                if (loseContext) {
                  loseContext.loseContext()
                }
              }
              if (container.contains(canvas)) {
                container.removeChild(canvas)
              }
            }
          } catch (e) {
            console.warn('[SmileStart] Error disposing renderer:', e)
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.smile-start-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: transparent;
}

.video-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  z-index: 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  background: #1a1a2e;
}

.video-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 1;
  pointer-events: none;
  background: #1a1a2e;
}

.canvas-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.ui-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 32px;
  pointer-events: none;
}

.ui-layer > * {
  pointer-events: auto;
}

/* Top: Smile Energy Indicator */
.ui-top {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.smile-energy-indicator {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 320px;
}

.smile-label {
  color: #334155;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.smile-bar-container {
  flex: 1;
  height: 10px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.smile-bar-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.15s cubic-bezier(0.4, 0, 0.2, 1), 
              background 0.3s ease;
  position: relative;
  overflow: hidden;
}

.smile-bar-inactive {
  background: linear-gradient(90deg, rgba(126, 180, 235, 1) 0%, rgba(96, 165, 250, 1) 100%);
}

.smile-bar-active {
  background: linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(217, 70, 239, 1) 100%);
  box-shadow: 0 0 12px rgba(168, 85, 247, 0.4);
}

.smile-percentage {
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  font-size: 15px;
  font-weight: 700;
  min-width: 48px;
  text-align: right;
  letter-spacing: 0.05em;
  transition: color 0.3s ease;
}

.smile-inactive {
  color: rgba(126, 180, 235, 1);
}

.smile-active {
  color: rgba(168, 85, 247, 1);
}

/* Center: Content */
.ui-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.loading-text {
  color: #22d3ee;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  font-size: 28px;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-shadow: 0 0 20px rgba(34, 211, 238, 0.6), 0 0 40px rgba(34, 211, 238, 0.3);
  z-index: 10;
  position: relative;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.intro-content {
  text-align: center;
  position: absolute;
  bottom: 128px;
}

.intro-title {
  font-size: 48px;
  font-weight: 100;
  color: white;
  margin-bottom: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

.intro-subtitle {
  color: #a5f3fc;
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.05em;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  margin-bottom: 24px;
}

.manual-start-btn {
  margin-top: 24px;
  padding: 12px 32px;
  background: linear-gradient(90deg, rgba(177, 110, 249, 0.8) 0%, rgba(126, 180, 235, 0.8) 100%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 30px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.manual-start-btn:hover {
  background: linear-gradient(90deg, rgba(177, 110, 249, 1) 0%, rgba(126, 180, 235, 1) 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.manual-start-btn:active {
  transform: translateY(0);
}

.menu-content {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.dimension-item {
  position: absolute;
  color: white;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

.dimension-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.dimension-line {
  width: 1px;
  height: 48px;
  background: linear-gradient(to top, rgba(234, 179, 8, 0), rgba(234, 179, 8, 0.5), rgba(234, 179, 8, 0));
  position: absolute;
  top: -32px;
  opacity: 0.5;
}

.dimension-label {
  padding: 8px 16px;
  color: #fef9c3;
  box-shadow: 0 0 15px rgba(234, 179, 8, 0.2);
  font-size: 16px;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

/* Bottom: Actions */

/* Fade In Animation */
.fade-in {
  animation: fadeIn 0.5s ease-out forwards;
  opacity: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

